import { Menu } from "primereact/menu";
import { PrimeIcons } from "primereact/api";
import { Button } from "primereact/button";
import { useRef, useState } from "react";

import { downloadPinnedAnswers, pinAnswersResponse } from "services";
import {
  labelDownload,
  labelGoToMessage,
  labelRename,
  labelRimuovi,
  labelSavedFor,
  labelShare,
  modalConfirmRemoveAllResponseSubTitle,
  modalConfirmRemoveAllResponseTitle,
  snackbarRemovedAnswerSubTitle,
  snackbarRemovedAnswerTitle,
  snackbarSeveritySuccess,
  subtitleDetailResponse,
} from "_constants";
import { SingleStepDialog } from "components/SingleStepDialog";
import { BodyModalDetailResponse } from "./BodyModalDetailResponse";
import { useConfirmDialogExit } from "hooks";
import { useSnackbar } from "providers/SnackbarProvider";
import { useAppDispatch, useAppSelector } from "store/store";
import {
  resetCurrentAnswer,
  resetLoadedStatus,
} from "store/slicer/GestioneSaveAnswerSlice";
import { ModalRenameAnswer, ModalShareAnswers } from "components/modals";
import { Tooltip } from "primereact/tooltip";
import { setOpenChat } from "store/slicer/GestioneChatSlice";
import { downloadFile, getUserId } from "utils";

export const ModalDetailResponse = ({
  modalVisibile,
  onClose,
  answerId,
  projectId,
}: {
  modalVisibile: boolean;
  onClose: Function;
  answerId: string;
  projectId: string;
}) => {
  const dispatch = useAppDispatch();
  const currentAnswer = useAppSelector(
    (state) => state.gestioneSaveAnswerSlice.currentAnswer
  );
  const [visible, setVisible] = useState(modalVisibile);
  const { showConfirmDialogDelete } = useConfirmDialogExit();
  const { showSnackbar } = useSnackbar();

  const menu = useRef<Menu>(null);

  const [openModalShareAnswer, setOpenModalShareAnswer] = useState<any>(false);
  const [openModalRenameAnswer, setOpenModalRenameAnswer] =
    useState<any>(false);

  const handleDismiss = () => {
    onClose();
    dispatch(resetCurrentAnswer());
  };

  const header = (
    <div className="width-95">
      <Tooltip
        target={`.title-detail`}
        position="bottom"
        mouseTrack
        mouseTrackLeft={10}
      />
      <h4
        className="title-detail ellipsis"
        data-pr-tooltip={currentAnswer?.title?.replace(/(\r\n|\n|\r)/gm, "")}
      >
        {currentAnswer?.title?.replace(/(\r\n|\n|\r)/gm, "")}
      </h4>
      <span className="text-base-regular">{subtitleDetailResponse}</span>
    </div>
  );

  const footer = (
    <div className="flex-container gap-4">
      <span className="text-base-regular">{labelSavedFor}</span>
      <span className="text-base-regular-semibold capitalize">
        {currentAnswer?.savedBy}
      </span>
    </div>
  );

  const items = [
    {
      label: labelRename,
      icon: PrimeIcons.PENCIL,
      command: () => {
        setOpenModalRenameAnswer(true);
      },
    },
    {
      label: labelDownload,
      icon: PrimeIcons.DOWNLOAD,
      command: () => {
        downloadPinnedAnswers({
          answersIdsList: [answerId],
          projectId: projectId,
        })
          .then((response) => {
            downloadFile(response, 1);
          })
          .catch();
      },
    },
    {
      label: labelShare,
      icon: PrimeIcons.SHARE_ALT,
      command: () => {
        setOpenModalShareAnswer(true);
      },
    },
    ...(currentAnswer?.savedById === getUserId()
      ? [
          {
            label: labelGoToMessage,
            icon: PrimeIcons.WINDOW_MAXIMIZE,
            command: () => {
              dispatch(setOpenChat(true));
              onClose();
            },
          },
        ]
      : []),
    {
      separator: true,
    },
    {
      label: labelRimuovi,
      icon: PrimeIcons.BOOKMARK,
      className: "delete-color",
      command: () => {
        removeAnswer();
      },
    },
  ];

  const removeAnswer = () => {
    showConfirmDialogDelete({
      title: modalConfirmRemoveAllResponseTitle,
      subtitle: modalConfirmRemoveAllResponseSubTitle,
      rejectCallback: confirmRemoveResponse,
    });
  };

  const confirmRemoveResponse = () => {
    pinAnswersResponse({
      projectId: projectId,
      pin: false,
      answersIds: [answerId],
    })
      .then((_) => {
        showSnackbar({
          severity: snackbarSeveritySuccess,
          summary: snackbarRemovedAnswerTitle,
          detail: snackbarRemovedAnswerSubTitle,
          life: 5000,
        });
        dispatch(resetLoadedStatus());
        onClose();
      })
      .catch((_) => {});
  };

  const action = (
    <>
      <Menu model={items} popup ref={menu} className="tiered-menu-custom" />
      <Button
        icon={PrimeIcons.ELLIPSIS_V}
        text
        className="meatball"
        onClick={(e) => {
          e.stopPropagation();
          menu.current!.toggle(e);
        }}
      />
    </>
  );

  return (
    <>
      <SingleStepDialog
        onHideCallback={handleDismiss}
        minHeight="507px"
        height="700px"
        width="1240px"
        size="auto"
        header={header}
        body={<BodyModalDetailResponse detailResp={currentAnswer!} />}
        footer={footer}
        display={visible}
        setVisible={setVisible}
        action={action}
        customClass="relative custom-dialog-modal modal-answer"
      />
      {openModalShareAnswer && (
        <ModalShareAnswers
          onClose={() => setOpenModalShareAnswer(false)}
          showDialog={openModalShareAnswer}
          detailId={answerId}
        />
      )}
      {openModalRenameAnswer && (
        <ModalRenameAnswer
          onClose={() => setOpenModalRenameAnswer(false)}
          showDialog={openModalRenameAnswer}
          answer={currentAnswer!}
        />
      )}
    </>
  );
};
